import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserCustomerAccess } from '../../_metronic/helpers/UserRoleHelpers';

interface CustomerSettings {
  locale?: string;
  autoPublishNoTextReviews?: boolean;
  autoApproveAi?: number;
  autoApproveMinimumRating?: number;
  autoApproveMood?: number;
  autoApprovePhoto?: number;
  autoReproveAi?: number;
  autoReproveMood?: number;
  autoReproveMaximumRating?: number;
  enableStoreReview?: boolean;
  enableSellerReview?: boolean;
  consentText?: string;
  consentLinkText?: string;
  consentLinkUrl?: string;
  explicitConsent?: boolean;
  explicitConsentAllReviews?: boolean;
  embedNpsSurvey?: boolean;
  showcaseRating?: string;
  homologationMode?: boolean;
  fullWidth?: boolean;
  starsV2?: boolean;
  summaryTemplate?: string;
  reviewsSort?: string;
  showAISummary?: string;
  aiSummaryAdditionalPrompt?: string;
  primaryColor?: string;
  customCSS?: string;
  customScriptBeforeInjection?: string;
  customScriptAfterInjection?: string;
  plugins?: string[];
  storeReviewFirst?: boolean;
  showMediaShelf?: boolean;
  debugOrders?: boolean;
  aggregateRatingV2?: boolean;
  reviewItemV2?: boolean;
  pdpPathSuffix?: string;
  enableThankyouEmail?: boolean;
  enableStoreReviewBadge?: boolean;
  enableStoreReviewCarousel?: boolean;
  defaultPublishReplies?: boolean;
  defaultSendReplyEmail?: boolean;
}

interface CustomerData {
  _id?: string;
  key?: string;
  name?: string;
  storeUrl?: string;
  logo?: string;
  apiUrl?: string;
  apiKey?: string;
  apiToken?: string;
  platform?: string;
  active?: boolean;
  enableQaAi?: boolean;
  settings?: CustomerSettings;
  [key: string]: any;
}

interface CustomerContextModel {
  customer: CustomerData | null;
  loading: boolean;
  error: string | null;
  isInactive: boolean;
  refetch: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextModel>({
  customer: null,
  loading: true,
  error: null,
  isInactive: false,
  refetch: async () => {},
});

export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth0();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isInactive, setIsInactive] = useState<boolean>(false);
  const [activeCustomer, setActiveCustomer] = useState<string | null>(null);

  const fetchCustomerData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsInactive(false);
      
      // Usar endpoint específico do dashboard que sempre retorna dados
      const result = await axios.get('dashboard/info');
      
      // Ensure settings object exists with defaults
      if (!result.data.settings) {
        result.data.settings = {
          defaultPublishReplies: false,
          defaultSendReplyEmail: false,
        };
      }
      
      // Verificar se o cliente está inativo pelos dados retornados
      // Exceção: Konfidency admins NÃO veem tela de aviso
      if (result.data.active === false && user) {
        const access = getUserCustomerAccess(user);
        if (!access.isKonfidencyAdmin) {
          setIsInactive(true);
        }
      }
      
      setCustomer(result.data);
    } catch (err: any) {
      console.error('Error loading customer data:', err);
      setError(err.message || 'Failed to load customer data');
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Monitor active customer changes
  useEffect(() => {
    const checkActiveCustomer = () => {
      const current = window.localStorage.getItem('activeCustomer');
      if (current !== activeCustomer) {
        setActiveCustomer(current);
        fetchCustomerData();
      }
    };

    // Initial load
    checkActiveCustomer();

    // Set up interval to check for customer changes
    const interval = setInterval(checkActiveCustomer, 1000);

    return () => clearInterval(interval);
  }, [activeCustomer, fetchCustomerData]);

  const value: CustomerContextModel = {
    customer,
    loading,
    error,
    isInactive,
    refetch: fetchCustomerData,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
}

export type { CustomerData, CustomerSettings, CustomerContextModel };

